/**
 * Reception Service
 * API integration for reception management operations
 *
 * @version 2025.1.0
 * @description Service for managing patient reception and attendance
 */

import apiClient from './apiClient';
import { ApiResponse } from '../types/common';
import {
  ReceptionAppointment,
  ReceptionListParams,
  ReceptionListResponse,
  ReceptionSummary,
  AttendanceStatus,
  AttendanceType,
} from '../types/reception';

// Re-export types for convenience
export * from '../types/reception';

/**
 * Helper to extract data from API response
 */
const extractData = <T>(response: ApiResponse<T>): T => {
  return response.data as T;
};

class ReceptionService {
  private baseUrl = '/appointments';

  // ============================================================================
  // LIST & SUMMARY
  // ============================================================================

  /**
   * Get reception list (appointments for reception management)
   */
  async getReceptionList(params: ReceptionListParams = {}): Promise<ReceptionListResponse> {
    const {
      page = 1,
      limit = 50,
      sortBy = 'hour',
      sortOrder = 'ASC',
      ...filters
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    // Add filters
    if (filters.date) queryParams.append('date', filters.date);
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.period && filters.period !== 'all') queryParams.append('period', filters.period);

    // Handle array filter (attendanceStatus)
    if (filters.attendanceStatus) {
      const statuses = Array.isArray(filters.attendanceStatus)
        ? filters.attendanceStatus
        : [filters.attendanceStatus];
      statuses.forEach(s => queryParams.append('attendanceStatus', s));
    }

    // apiClient.get returns response.data directly, so:
    // response = { message: "...", data: [...], pagination: {...} }
    const response = await apiClient.get<ReceptionAppointment[]>(
      `${this.baseUrl}/reception?${queryParams.toString()}`
    );

    const appointments = response.data ?? [];
    const pag = response.pagination;

    return {
      appointments,
      total: (pag?.total || appointments.length) ?? 0,
      page: pag?.page || page,
      limit: pag?.limit || limit,
      totalPages: pag?.totalPages || Math.ceil((pag?.total || appointments.length) / limit) || 0,
    };
  }

  /**
   * Get reception summary (counts by status and period)
   */
  async getReceptionSummary(clinicId: number, date: string): Promise<ReceptionSummary> {
    const queryParams = new URLSearchParams({
      clinicId: clinicId.toString(),
      date,
    });

    const response = await apiClient.get(`${this.baseUrl}/reception/summary?${queryParams.toString()}`);
    return extractData(response);
  }

  // ============================================================================
  // ATTENDANCE STATUS ACTIONS
  // ============================================================================

  /**
   * Check-in appointment (patient arrived)
   */
  async checkIn(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('📥 Check-in appointment:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/check-in`);
    console.log('✅ Check-in successful');
    return extractData(response);
  }

  /**
   * Set patient as triaged
   */
  async setTriaged(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('🩺 Setting patient as triaged:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/set-triaged`);
    console.log('✅ Patient triaged');
    return extractData(response);
  }

  /**
   * Save triage data and set patient as triaged (combined operation)
   */
  async saveTriageOnly(appointmentId: number, triageData: {
    bloodPressureSystolic?: number | null;
    bloodPressureDiastolic?: number | null;
    heartRate?: number | null;
    temperature?: number | null;
    weight?: number | null;
    height?: number | null;
    oxygenSaturation?: number | null;
    observations?: string | null;
  }): Promise<ReceptionAppointment> {
    console.log('🩺 Saving triage only:', appointmentId);
    // First save triage data
    await this.saveTriage(appointmentId, triageData);
    // Then set as triaged
    const result = await this.setTriaged(appointmentId);
    console.log('✅ Triage only completed');
    return result;
  }

  /**
   * Start attendance (doctor started seeing patient)
   */
  async startAttendance(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('▶️ Starting attendance:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/start-attendance`);
    console.log('✅ Attendance started');
    return extractData(response);
  }

  /**
   * Complete attendance (doctor finished seeing patient)
   * @param attendanceType - Optional: 'FULL' or 'QUICK'
   */
  async completeAttendance(appointmentId: number, attendanceType?: AttendanceType): Promise<ReceptionAppointment> {
    console.log('✔️ Completing attendance:', appointmentId, attendanceType ? `(${attendanceType})` : '');
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/complete-attendance`, {
      attendanceType,
    });
    console.log('✅ Attendance completed');
    return extractData(response);
  }

  /**
   * Mark appointment as no-show (patient didn't come)
   */
  async markNoShow(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('❌ Marking as no-show:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/no-show`);
    console.log('✅ Marked as no-show');
    return extractData(response);
  }

  /**
   * Update queue number manually
   */
  async updateQueueNumber(appointmentId: number, queueNumber: number): Promise<ReceptionAppointment> {
    console.log('🔢 Updating queue number:', appointmentId, '->', queueNumber);
    const response = await apiClient.put(`${this.baseUrl}/${appointmentId}/queue-number`, {
      queueNumber,
    });
    console.log('✅ Queue number updated');
    return extractData(response);
  }

  /**
   * Reset queue (clear queue number)
   */
  async resetQueue(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('🔄 Resetting queue:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/reset-queue`);
    console.log('✅ Queue reset');
    return extractData(response);
  }

  // ============================================================================
  // TRIAGE
  // ============================================================================

  /**
   * Save triage data for an appointment
   */
  async saveTriage(appointmentId: number, triageData: {
    bloodPressureSystolic?: number | null;
    bloodPressureDiastolic?: number | null;
    heartRate?: number | null;
    temperature?: number | null;
    weight?: number | null;
    height?: number | null;
    oxygenSaturation?: number | null;
    observations?: string | null;
  }): Promise<unknown> {
    console.log('📋 Saving triage data:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/triage`, triageData);
    console.log('✅ Triage saved');
    return extractData(response);
  }

  /**
   * Get triage data for an appointment
   */
  async getTriage(appointmentId: number): Promise<unknown> {
    const response = await apiClient.get(`${this.baseUrl}/${appointmentId}/triage`);
    return extractData(response);
  }

  // ============================================================================
  // PDF GENERATION
  // ============================================================================

  /**
   * Generate Guide PDF (Guia de Atendimento)
   */
  async generateGuide(appointmentId: number): Promise<{ pdfPath: string }> {
    console.log('📄 Generating guide:', appointmentId);
    const response = await apiClient.post(`/medical-records/${appointmentId}/generate-guide`);
    console.log('✅ Guide generated');
    return extractData(response);
  }

  /**
   * Call a patient (updates queueCalledAt for TV display panel)
   */
  async callPatient(appointmentId: number): Promise<ReceptionAppointment> {
    console.log('📢 Calling patient:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/call`);
    console.log('✅ Patient called');
    return extractData(response);
  }

  // ============================================================================
  // EXPORT
  // ============================================================================

  /**
   * Export reception report to Excel or CSV
   * @param params - Filter parameters
   * @param format - Export format: 'xlsx' (default) or 'csv'
   */
  async exportReport(params: ReceptionListParams, format: 'xlsx' | 'csv' = 'xlsx'): Promise<Blob> {
    const queryParams = new URLSearchParams();

    // Add format parameter
    queryParams.append('format', format);

    if (params.date) queryParams.append('date', params.date);
    if (params.clinicId) queryParams.append('clinicId', params.clinicId.toString());
    if (params.companyId) queryParams.append('companyId', params.companyId.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.period && params.period !== 'all') queryParams.append('period', params.period);

    if (params.attendanceStatus) {
      const statuses = Array.isArray(params.attendanceStatus)
        ? params.attendanceStatus
        : [params.attendanceStatus];
      statuses.forEach(s => queryParams.append('attendanceStatus', s));
    }

    // httpClient.get returns response.data directly, which is the Blob when responseType is 'blob'
    const response = await apiClient.get(`${this.baseUrl}/reception/export?${queryParams.toString()}`, {
      responseType: 'blob',
    });

    // Ensure we return a proper Blob object
    if (response instanceof Blob) {
      return response;
    }
    // Handle case where response might be wrapped or need conversion
    return new Blob([response as unknown as BlobPart]);
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get status label for display
   */
  getStatusLabel(status: AttendanceStatus): string {
    const labels: Record<AttendanceStatus, string> = {
      [AttendanceStatus.NOT_ATTENDED]: 'Aguardando',
      [AttendanceStatus.CHECKED_IN]: 'Check-in',
      [AttendanceStatus.TRIAGED]: 'Triagem Feita',
      [AttendanceStatus.IN_PROGRESS]: 'Em Atendimento',
      [AttendanceStatus.COMPLETED]: 'Atendido',
      [AttendanceStatus.NO_SHOW]: 'Faltou',
    };
    return labels[status] || status;
  }

  /**
   * Get status color for badge
   */
  getStatusColor(status: AttendanceStatus): 'default' | 'info' | 'warning' | 'success' | 'error' | 'secondary' {
    const colors: Record<AttendanceStatus, 'default' | 'info' | 'warning' | 'success' | 'error' | 'secondary'> = {
      [AttendanceStatus.NOT_ATTENDED]: 'default',
      [AttendanceStatus.CHECKED_IN]: 'info',
      [AttendanceStatus.TRIAGED]: 'secondary',
      [AttendanceStatus.IN_PROGRESS]: 'warning',
      [AttendanceStatus.COMPLETED]: 'success',
      [AttendanceStatus.NO_SHOW]: 'error',
    };
    return colors[status] || 'default';
  }

  /**
   * Check if action is available for status
   */
  canCheckIn(status: AttendanceStatus): boolean {
    return status === AttendanceStatus.NOT_ATTENDED;
  }

  canTriage(status: AttendanceStatus): boolean {
    return status === AttendanceStatus.CHECKED_IN;
  }

  canStartAttendance(status: AttendanceStatus): boolean {
    return status === AttendanceStatus.CHECKED_IN || status === AttendanceStatus.TRIAGED;
  }

  canCompleteAttendance(status: AttendanceStatus): boolean {
    return status === AttendanceStatus.IN_PROGRESS;
  }

  canMarkNoShow(status: AttendanceStatus): boolean {
    return status === AttendanceStatus.NOT_ATTENDED;
  }

  /**
   * Get available actions for a status
   */
  getAvailableActions(status: AttendanceStatus): Array<'check-in' | 'triage' | 'start-attendance' | 'complete-attendance' | 'no-show'> {
    const actions: Array<'check-in' | 'triage' | 'start-attendance' | 'complete-attendance' | 'no-show'> = [];

    if (this.canCheckIn(status)) actions.push('check-in');
    if (this.canTriage(status)) actions.push('triage');
    if (this.canStartAttendance(status)) actions.push('start-attendance');
    if (this.canCompleteAttendance(status)) actions.push('complete-attendance');
    if (this.canMarkNoShow(status)) actions.push('no-show');

    return actions;
  }

  /**
   * Format queue number for display (e.g., 001, 002, etc.)
   */
  formatQueueNumber(queueNumber: number | null): string {
    if (queueNumber === null) return '-';
    return String(queueNumber).padStart(3, '0');
  }

  // ============================================================================
  // KIT DE ATENDIMENTO
  // ============================================================================

  /**
   * Generate public Kit de Atendimento link for sharing
   * @param appointmentId - Appointment ID
   * @returns Object with link and expiration date
   */
  async generateKitLink(appointmentId: number): Promise<{ link: string; expiresAt: string }> {
    console.log('🔗 Generating kit link:', appointmentId);
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/generate-kit-link`);
    console.log('✅ Kit link generated');
    return extractData<{ link: string; expiresAt: string }>(response);
  }

  /**
   * Format time for display (HH:MM)
   */
  formatTime(hour: string | null, minute: string | null): string {
    if (!hour) return '-';
    const h = hour.padStart(2, '0');
    const m = (minute || '0').padStart(2, '0');
    return `${h}:${m}`;
  }

  /**
   * Check if time is in the morning (before 12:00)
   */
  isMorning(hour: string | null): boolean {
    if (!hour) return false;
    return parseInt(hour, 10) < 12;
  }

  /**
   * Check if time is in the afternoon (12:00 or later)
   */
  isAfternoon(hour: string | null): boolean {
    if (!hour) return false;
    return parseInt(hour, 10) >= 12;
  }
}

// Export singleton instance
export const receptionService = new ReceptionService();
export default receptionService;
